import {
  CAPABILITY_MANIFEST,
  type CapabilityName,
  type CommandName,
  type ErrorCodeOf,
  type InputOf,
  type KeyedCommandName,
  type OutputOf,
  type QueryName,
} from './generated/contracts';

export type {
  CapabilityContracts,
  CapabilityName,
  CommandName,
  ErrorCodeOf,
  InputOf,
  KeyedCommandName,
  OutputOf,
  QueryName,
} from './generated/contracts';
export { CAPABILITY_MANIFEST } from './generated/contracts';

/** The rules and formats every bidding screen needs, on any platform. */
export {
  auctionPollMs,
  type AuctionView,
  type BidStatus,
  bidPicks,
  bidStatus,
  type BidTone,
  endsAtLabel,
  formatCents,
  formatRemaining,
  nextBidAction,
  placeBidWithRetry,
  type Position,
  remainingMs,
  sellerLabel,
  timeAgo,
} from './auctions';

/** The rules every live-show screen needs — the viewer's and the studio's, on any platform. */
export {
  appendOwnMessage,
  applyRoomMessage,
  blockedBecause,
  CHAT_WINDOW,
  type ChatMessage,
  describeChannel,
  HEARTBEAT_MS,
  type LineupEntry,
  type Lot,
  lotsByListing,
  nameHash,
  reactionCount,
  SELLER_STEP_LABEL,
  type SellerListing,
} from './shows';

/** Choosing a category from Shopify's tree (D59): search, suggestions, and a category's form. */
export {
  type CategoryAttribute,
  type CategoryEntry,
  type CategoryForm,
  type Taxonomy,
  type TaxonomyCategory,
  categoryOptions,
  categoryPath,
  categoryTrail,
  formFromCategory,
  formFromTaxonomy,
  searchCategories,
  suggestedCategories,
} from './categories';

/** Order history and detail for sellers and buyers (GO-276 to GO-279): the words for each status, filter and line. */
export {
  BUYER_ORDER_STATUS_LABELS,
  type BuyerOrderDetail,
  type BuyerOrderRow,
  type BuyerOrderStatus,
  type BuyerOrders,
  ORDER_FEE_LABELS,
  ORDER_TIMELINE_LABELS,
  SELLER_ORDER_TABS,
  type SellerOrderTab,
  orderDate,
  orderDateTime,
  orderMoney,
  orderProductsLine,
  productCountLabel,
  ORDER_PRODUCT_KIND_LABELS,
  SELLER_ORDER_FILTER_LABELS,
  SELLER_ORDER_STATUS_LABELS,
  type SellerOrderDetail,
  type SellerOrderFilter,
  type SellerOrderProduct,
  type SellerOrderRow,
  type SellerOrderStatus,
  type SellerOrders,
  orderShowLabel,
} from './orders';

/** Seller onboarding: the checklist, with a missed Stripe verdict asked for first. */
export { type SellerChecklist, sellerChecklist } from './sellers';

/** The seller CSV import's screen rules (D55) — the phone's screens and the web harness. */
export {
  type AttributeLabel,
  type CategoryQuestion,
  type CategoryResolution,
  type ColumnChoices,
  canPublish,
  categoryColumns,
  categoryQuestion,
  categoryValues,
  columnsToReview,
  type ImportJob,
  type ImportMapping,
  type ImportProposal,
  type ImportRow,
  type ImportStage,
  type ImportTarget,
  importStage,
  initialChoices,
  mappingProblems,
  type ProposedColumn,
  REVIEW_CONFIDENCE,
  rowProblems,
  targetLabel,
  targetOptions,
  toMapping,
  UNCATEGORIZED,
} from './imports';

/** The AppSync Events protocol, framework-free — each app wraps it in its own hook. */
export {
  type ChannelState,
  type ChannelStatus,
  type RealtimeEndpoint,
  type SocketFactory,
  type SocketLike,
  subscribeChannel,
} from './realtime';

/**
 * The typed capability client — what every UI surface talks to (Lane G: "shared
 * generated types from registry").
 *
 * It is thin on purpose. The registry owns validation, authorization, rate limits and
 * idempotency; duplicating any of it here would create a second answer, and the one
 * that disagrees is always the client. What the client *does* own is the transport
 * rules that would otherwise be re-derived in every screen:
 *
 * - Commands `POST /v1/c/{name}`; queries `GET /v1/q/{name}` with URL-encoded JSON
 *   input, falling back to POST when the URL would get impractically long
 *   (capabilities.md §1.7 — GET is what makes guest reads cacheable at the edge).
 * - **A keyed command will not compile without an idempotency key.** Money-moving and
 *   irreversible commands declare `idempotency: 'keyed'`, and the server answers
 *   `IDEMPOTENCY_KEY_REQUIRED` without one. Finding that out at runtime, in a
 *   payment flow, is the wrong place to find out.
 * - Errors arrive as the standard envelope and are thrown as `CapabilityClientError`
 *   with `code` narrowed to the codes that capability declares, plus `retryable` and
 *   the `requestId` to quote in a bug report.
 */

/**
 * An error, as a screen has to render it.
 *
 * Lives here rather than in either client because both need exactly this and the shape is
 * about `CapabilityClientError`, not about a platform: the server already sent a human
 * message, a code and a request id, and the easy version — `catch` and log — produces a UI
 * that is permanently loading when something fails.
 *
 * **No React in this package**, which is a constraint rather than a preference: the mobile
 * bundler resolves shared packages with hierarchical lookup off, so anything imported here
 * would have to be resolvable from `apps/mobile/node_modules` and is not. Hooks stay in
 * the apps; this is the part that is genuinely common.
 */
export interface DescribedError {
  message: string;
  code?: string;
  /** Quote this in a bug report — it is the one thing that finds the request in the logs. */
  requestId?: string;
}

/**
 * Codes whose server message is written for an agent or a log rather than for a person.
 * The pipeline's rate-limit message names the capability and its rate class, which a
 * buyer mid-bid should not have to read.
 */
const PEOPLE_MESSAGES: Record<string, string> = {
  RATE_LIMITED: 'Too many requests. Wait a moment and try again.',
};

/**
 * What each browser's `fetch` throws when the request never got an answer: Chrome, Firefox,
 * Safari and React Native. Exact messages, because a `TypeError` can also be a bug, which
 * should not be passed off as a bad connection.
 */
const NETWORK_FAILURE =
  /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?|Network request failed)$/;

export const UNREACHABLE = 'Couldn’t reach Going Once. Check your connection and try again.';

export function describeError(error: unknown): DescribedError {
  if (error instanceof CapabilityClientError) {
    return {
      message: PEOPLE_MESSAGES[error.code] ?? error.message,
      code: error.code,
      ...(error.requestId ? { requestId: error.requestId } : {}),
    };
  }
  if (error instanceof TypeError && NETWORK_FAILURE.test(error.message)) return { message: UNREACHABLE };
  return { message: error instanceof Error ? error.message : String(error) };
}

const URL_INPUT_LIMIT = 1500;

/**
 * Call once more, after `delayMs`, when the first attempt failed with an error its server
 * marked retryable — for a read whose first call can lose a race with something warming up,
 * like the Discovery feed waking its collection from zero (`SEARCH_UNAVAILABLE`, D63). Only
 * once: a second refusal is a real outage, and a screen should say so rather than spin.
 */
export async function retryOnce<T>(call: () => Promise<T>, delayMs = 1_000): Promise<T> {
  try {
    return await call();
  } catch (error) {
    if (!(error instanceof CapabilityClientError) || !error.retryable) throw error;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    return call();
  }
}

export interface ClientOptions {
  /** e.g. `https://api.goingonce.example` or `http://localhost:4000` — no trailing `/v1`. */
  baseUrl: string;
  /**
   * Bearer token supplier, called per request so a refreshed token is picked up
   * without rebuilding the client. Return undefined for a guest caller.
   */
  getToken?: () => string | undefined | Promise<string | undefined>;
  /** Injected for tests and for React Native, where `fetch` is the platform's. */
  fetch?: typeof globalThis.fetch;
  /** Extra headers on every request (e.g. a client version for support triage). */
  headers?: Record<string, string>;
  /**
   * Where listing media is served from — the distribution's `/media` path, which the
   * `MediaBaseUrl` output of `EdgeStack` gives you verbatim. Same origin as `baseUrl`,
   * so nothing here preflights.
   *
   * Capabilities return object **keys**, not URLs, because the URL depends on which
   * distribution is in front of the bucket and that is deployment configuration. Baking
   * it into a response would put an environment's hostname inside a contract and make
   * the same listing serialize differently per environment. Composing it is the
   * client's job, and `mediaUrl` is the one place it happens.
   */
  mediaBaseUrl?: string;
}

export interface RequestOptions {
  /** Required for staff invocations of high-audit capabilities; sent as `x-go-reason`. */
  reason?: string;
  /**
   * The ticket that asked for a staff action (R210); sent as `x-go-ticket`. Required for
   * staff invocations of high-audit commands (TICKET_REQUIRED without one) and ignored for
   * any other caller. A ticket id or an http(s) link, printable ASCII, at most 512
   * characters (`isTicketRef` in `@going-once/contracts`), or the call fails VALIDATION_FAILED.
   */
  ticketRef?: string;
  signal?: AbortSignal;
}

export interface KeyedRequestOptions extends RequestOptions {
  /**
   * Stable per logical operation, **not** per attempt: the point is that a retry
   * carries the same key. A fresh key on retry is a second charge.
   */
  idempotencyKey: string;
}

/** What a call returns when the caller wants the envelope, not just the output. */
export interface CallResult<T> {
  output: T;
  requestId: string;
  /** True when the server replayed a cached result for this idempotency key. */
  replayed: boolean;
}

export class CapabilityClientError<Code extends string = string> extends Error {
  constructor(
    readonly code: Code,
    message: string,
    readonly retryable: boolean,
    readonly status: number,
    readonly requestId: string | undefined,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'CapabilityClientError';
  }

  /** Narrowing helper, so a caller can branch on a code without a cast. */
  is<N extends CapabilityName>(...codes: ErrorCodeOf<N>[]): boolean {
    return (codes as string[]).includes(this.code);
  }
}

/** Options type for a command: the idempotency key is required exactly when the server requires it. */
type CommandOptions<N extends CommandName> = N extends KeyedCommandName ? KeyedRequestOptions : RequestOptions;

export interface CapabilityClient {
  query<N extends QueryName>(name: N, input: InputOf<N>, opts?: RequestOptions): Promise<OutputOf<N>>;
  command<N extends CommandName>(name: N, input: InputOf<N>, opts: CommandOptions<N>): Promise<OutputOf<N>>;
  /** Same calls, with the request id and replay flag — for money flows and support tooling. */
  queryWithMeta<N extends QueryName>(
    name: N,
    input: InputOf<N>,
    opts?: RequestOptions,
  ): Promise<CallResult<OutputOf<N>>>;
  commandWithMeta<N extends CommandName>(
    name: N,
    input: InputOf<N>,
    opts: CommandOptions<N>,
  ): Promise<CallResult<OutputOf<N>>>;
  /**
   * The URL for a media object key returned by a capability (`coverMediaKey`,
   * `mediaKeys`). Throws if no `mediaBaseUrl` was configured, rather than returning a
   * relative path that renders as a broken image in one environment and works in
   * another.
   */
  mediaUrl(key: string): string;
}

export function createClient(options: ClientOptions): CapabilityClient {
  const fetchImpl = options.fetch ?? globalThis.fetch;
  if (typeof fetchImpl !== 'function') {
    throw new Error('No fetch implementation available — pass one via ClientOptions.fetch.');
  }
  const base = options.baseUrl.replace(/\/+$/, '');
  const mediaBase = options.mediaBaseUrl?.replace(/\/+$/, '');

  async function call<N extends CapabilityName>(
    name: N,
    input: InputOf<N>,
    opts: RequestOptions & { idempotencyKey?: string },
  ): Promise<CallResult<OutputOf<N>>> {
    const entry = CAPABILITY_MANIFEST[name];
    const isCommand = entry.kind === 'command';

    // Fail before the network on the mistake the type system catches for typed
    // callers but not for a `string` name coming out of, say, a generic form.
    if (entry.idempotency === 'keyed' && !opts.idempotencyKey) {
      throw new CapabilityClientError(
        'IDEMPOTENCY_KEY_REQUIRED',
        `"${name}" is idempotency-keyed; supply a stable idempotencyKey (the same value on every retry).`,
        false,
        0,
        undefined,
      );
    }

    const token = await options.getToken?.();
    const headers: Record<string, string> = {
      accept: 'application/json',
      ...options.headers,
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(opts.reason ? { 'x-go-reason': opts.reason } : {}),
      ...(opts.ticketRef ? { 'x-go-ticket': opts.ticketRef } : {}),
      ...(opts.idempotencyKey ? { 'idempotency-key': opts.idempotencyKey } : {}),
    };

    const encoded = JSON.stringify(input ?? {});
    /**
     * **GET only for a query that declares a cache policy**, which is the one reason GET exists
     * (`CachePolicy` in the registry). Everything else goes as POST, with the input in the body.
     * A URL is the part of a request that browsers keep in history and every proxy, CDN and
     * access log records, so an input such as an invitation code (`sellers.invitation.get`)
     * must never be in one. Deciding this by cache policy rather than by a per-capability
     * "secret" flag means a new query is safe by default. The gateway agrees: a query with no
     * cache policy has no GET route, except a frozen list that builds installed before this
     * change still call (`servesGet` in `packages/registry/src/transport.ts`).
     */
    const useGet = !isCommand && 'cache' in entry && encoded.length <= URL_INPUT_LIMIT;
    const path = `${base}/v1/${isCommand ? 'c' : 'q'}/${name}`;
    const request: RequestInit = useGet
      ? { method: 'GET', headers, ...(opts.signal ? { signal: opts.signal } : {}) }
      : {
          method: 'POST',
          headers: { ...headers, 'content-type': 'application/json' },
          body: encoded,
          ...(opts.signal ? { signal: opts.signal } : {}),
        };
    const url = useGet && encoded !== '{}' ? `${path}?input=${encodeURIComponent(encoded)}` : path;

    const response = await fetchImpl(url, request);
    const requestId = response.headers.get('x-go-request-id') ?? undefined;
    const body = await readJson(response);

    if (!response.ok) {
      const envelope = gatewayRefusal(response.status, body, token) ?? ((body ?? {}) as ErrorEnvelope);
      throw new CapabilityClientError(
        envelope.code ?? 'INTERNAL_ERROR',
        envelope.message ?? `Request to "${name}" failed with status ${response.status}.`,
        envelope.retryable ?? response.status >= 500,
        response.status,
        // Header first, envelope second: an infrastructure failure (a gateway 502, a
        // timeout at the edge) has neither, and the difference between "our error" and
        // "something in front of us" is exactly what a support thread needs to know.
        requestId ?? envelope.requestId,
        envelope.details,
      );
    }

    return {
      output: body as OutputOf<N>,
      requestId: requestId ?? '',
      replayed: response.headers.get('x-go-idempotent-replay') === 'true',
    };
  }

  return {
    async query(name, input, opts = {}) {
      return (await call(name, input, opts)).output;
    },
    async command(name, input, opts) {
      return (await call(name, input, opts as RequestOptions & { idempotencyKey?: string })).output;
    },
    queryWithMeta(name, input, opts = {}) {
      return call(name, input, opts);
    },
    commandWithMeta(name, input, opts) {
      return call(name, input, opts as RequestOptions & { idempotencyKey?: string });
    },
    mediaUrl(key) {
      if (!mediaBase) {
        throw new Error('No mediaBaseUrl configured — set it on createClient to render listing media.');
      }
      // Each path segment is encoded separately: the keys we mint are safe, but a key
      // is data from a response and encoding the whole thing would turn its slashes
      // into %2F and 404 every image.
      return `${mediaBase}/${key.split('/').map(encodeURIComponent).join('/')}`;
    },
  };
}

/**
 * A non-JSON body is a real case — a gateway 502 is HTML, and a dropped connection is
 * nothing at all. Returning undefined lets the error path report the status honestly
 * instead of throwing a parse error over the top of it.
 */
async function readJson(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => '');
  if (text === '') return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

interface ErrorEnvelope {
  code?: string;
  message?: string;
  retryable?: boolean;
  details?: unknown;
  requestId?: string;
}

/**
 * A refusal from something in front of the API, which answers before any of our code runs
 * and so carries none of the envelope. The client puts back the code the API itself would
 * have sent, so a screen that branches on the code behaves the same whichever side said no.
 *
 * - **401** is the gateway's JWT authorizer, `{"message":"Unauthorized"}` (D31 addendum,
 *   2026-10-01). An HTTP API cannot customize it. No token is `AUTHENTICATION_REQUIRED`; a
 *   refused token is `INVALID_TOKEN`. The Sell tab, for a guest, depends on this.
 * - **429** is the gateway's own throttle. It is the same fact as the pipeline's
 *   `RATE_LIMITED`, and retryable, which a bare status read as a non-retryable internal error.
 * - **403** with no envelope is the edge's web ACL: the per-IP rate rule or a managed rule
 *   (`EdgeStack`). CloudFront answers with an HTML page. Our own 403s always carry a code.
 */
function gatewayRefusal(status: number, body: unknown, token: string | undefined): ErrorEnvelope | undefined {
  if (typeof body === 'object' && body !== null && 'code' in body) return undefined;
  if (status === 401) {
    return token
      ? {
          code: 'INVALID_TOKEN',
          message: 'The bearer token was rejected. Refresh the session and retry.',
          retryable: false,
        }
      : { code: 'AUTHENTICATION_REQUIRED', message: 'Sign in to do this.', retryable: false };
  }
  if (status === 429) {
    return { code: 'RATE_LIMITED', message: 'Too many requests. Wait a moment and try again.', retryable: true };
  }
  if (status === 403) {
    return {
      code: 'REQUEST_BLOCKED',
      message: 'This request was blocked. If it keeps happening, contact support.',
      retryable: false,
    };
  }
  return undefined;
}
